interface ArtworkLinkInput {
  artworkId: string;
  userId: string;
}

interface BuildArtworkDetailLinkOptions {
  currentUserId: string | null;
  isDelegateOf: (delegatorUserId: string) => boolean;
}

interface ArtworkDetailLink {
  to: string;
  routePrefix: '/object-details' | '/public/object-details';
}

/**
 * Resolve the correct detail-page route for an artwork given the viewer's
 * relationship to its owner.
 *
 * Three-way branch (see PR fixing search→detail for delegates):
 *   - Viewer owns the artwork → authenticated route, no owner param.
 *   - Viewer is a logged-in delegate of the owner → authenticated route +
 *     ``?owner=<ownerUserId>`` so the backend resolves to the delegator.
 *   - Otherwise (logged-out, or logged-in but unrelated) → public route.
 *
 * Returned ``routePrefix`` matches the prefix encoded in ``to`` and is meant
 * to be stashed in ``location.state.routePrefix`` for back-navigation flows.
 */
export const buildArtworkDetailLink = (
  artwork: ArtworkLinkInput,
  { currentUserId, isDelegateOf }: BuildArtworkDetailLinkOptions
): ArtworkDetailLink => {
  if (currentUserId !== null && artwork.userId === currentUserId) {
    return {
      to: `/object-details/${artwork.artworkId}`,
      routePrefix: '/object-details',
    };
  }

  if (currentUserId !== null && isDelegateOf(artwork.userId)) {
    return {
      to: `/object-details/${artwork.artworkId}?owner=${encodeURIComponent(artwork.userId)}`,
      routePrefix: '/object-details',
    };
  }

  return {
    to: `/public/object-details/${artwork.artworkId}`,
    routePrefix: '/public/object-details',
  };
};
